import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { getRequest, postRequest } from '../../api/ApiController';
import { LocaleContext } from '../../components/LocaleContext';
import UtilContext from '../../components/UtilContext';

/**
 * Legacy/unused flow: retained for backward compatibility in tab registry.
 * Main "Nové zadanie" flow now lives in CrmCommunicationNewTaskHub (inline accordion).
 *
 * @param {{ ticketType: 'announcement'|'acquisition'|'quote_request' }} props
 */
export default function CrmCommunicationTicketForm({ ticketType }) {
  const { t } = useContext(LocaleContext);
  const { toast } = useContext(UtilContext);
  const [title, setTitle] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [scheduled, setScheduled] = useState('');
  const [submitterSame, setSubmitterSame] = useState(true);
  const [primaryCompanyId, setPrimaryCompanyId] = useState(null);
  const [primaryCompanyContactId, setPrimaryCompanyContactId] = useState(null);
  const [primaryContactOptions, setPrimaryContactOptions] = useState([]);
  /** @type {Array<{ company_id: number, company_contact_id: number | null }>} */
  const [recipientRows, setRecipientRows] = useState([]);
  const [contactsByCompany, setContactsByCompany] = useState({});
  const loadedContactCompaniesRef = useRef(new Set());
  const [saving, setSaving] = useState(false);
  /** @type {Array<{ id: number, name: string }>} */
  const [ticketCompanies, setTicketCompanies] = useState([]);

  const type = ticketType || 'announcement';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getRequest('/api/crm_communication_catalog/companies_for_ticket');
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setTicketCompanies(rows);
        }
      } catch {
        if (!cancelled) {
          setTicketCompanies([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const companyOptions = useMemo(
    () => ticketCompanies.map((c) => ({ label: c.name || `ID ${c.id}`, value: c.id })),
    [ticketCompanies],
  );

  const titleLabel = t(`CrmCommunication.Form.FieldTitle.${type}`);
  const bodyLabel = t(`CrmCommunication.Form.FieldBody.${type}`);

  const companyName = useCallback(
    (id) => {
      const c = ticketCompanies.find((x) => Number(x.id) === Number(id));
      return c?.name || `ID ${id}`;
    },
    [ticketCompanies],
  );

  const recipientCompanyIds = useMemo(() => recipientRows.map((r) => r.company_id), [recipientRows]);

  useEffect(() => {
    const ids = [...new Set(recipientRows.map((r) => r.company_id))].filter(Boolean);
    const todo = ids.filter((id) => !loadedContactCompaniesRef.current.has(id));
    if (todo.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      const updates = {};
      await Promise.all(
        todo.map(async (cid) => {
          try {
            const res = await getRequest(`/api/crm_communication_catalog/company_contacts?company_id=${cid}`);
            updates[cid] = Array.isArray(res?.data) ? res.data : [];
          } catch {
            updates[cid] = [];
          }
        }),
      );
      if (cancelled) return;
      todo.forEach((id) => loadedContactCompaniesRef.current.add(id));
      setContactsByCompany((prev) => ({ ...prev, ...updates }));
    })();
    return () => {
      cancelled = true;
    };
  }, [recipientRows]);

  useEffect(() => {
    if (type === 'announcement') {
      setPrimaryContactOptions([]);
      setPrimaryCompanyContactId(null);
      return undefined;
    }
    if (!primaryCompanyId) {
      setPrimaryContactOptions([]);
      setPrimaryCompanyContactId(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getRequest(`/api/crm_communication_catalog/company_contacts?company_id=${primaryCompanyId}`);
        if (!cancelled) {
          setPrimaryContactOptions(Array.isArray(res?.data) ? res.data : []);
          setPrimaryCompanyContactId(null);
        }
      } catch {
        if (!cancelled) {
          setPrimaryContactOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [primaryCompanyId, type]);

  const onRecipientCompaniesChange = useCallback((ids) => {
    const arr = ids || [];
    setRecipientRows((prev) => {
      const prevMap = new Map(prev.map((r) => [r.company_id, r.company_contact_id]));
      return arr.map((cid) => ({
        company_id: cid,
        company_contact_id: prevMap.has(cid) ? prevMap.get(cid) : null,
      }));
    });
  }, []);

  const submit = useCallback(async () => {
    if (!title.trim()) {
      toast?.current?.show({ severity: 'warn', summary: t('CrmCommunication.Form.ValidationTitle'), life: 2500 });
      return;
    }
    if (type === 'announcement' && recipientRows.length === 0) {
      toast?.current?.show({ severity: 'warn', summary: t('CrmCommunication.Form.ValidationRecipients'), life: 2800 });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type,
        title: title.trim(),
        body_html: bodyHtml || null,
        scheduled_send_at: scheduled ? new Date(scheduled).toISOString() : null,
        submitter_same_as_sender: submitterSame,
        primary_company_id: primaryCompanyId != null ? primaryCompanyId : null,
        primary_company_contact_id: primaryCompanyContactId != null ? primaryCompanyContactId : null,
      };
      if (type === 'announcement') {
        payload.recipients = recipientRows.map(({ company_id, company_contact_id }) => {
          const row = { company_id };
          if (company_contact_id != null) {
            row.company_contact_id = company_contact_id;
          }
          return row;
        });
      }
      const res = await postRequest('/api/crm_communication_tickets', payload, true);
      if (res?.status === 200 && res?.data?.id) {
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('ServiceTask.RecordSaved') });
        setTitle('');
        setBodyHtml('');
        setScheduled('');
        setSubmitterSame(true);
        setPrimaryCompanyId(null);
        setPrimaryCompanyContactId(null);
        setRecipientRows([]);
      } else {
        throw new Error(JSON.stringify(res?.errors || res?.message || res));
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setSaving(false);
    }
  }, [
    bodyHtml,
    primaryCompanyContactId,
    primaryCompanyId,
    recipientRows,
    scheduled,
    submitterSame,
    t,
    title,
    toast,
    type,
  ]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-50">
      <header className="shrink-0 bg-neutral-900 px-4 py-3 text-base font-semibold text-white shadow">{t(`CrmCommunication.Form.Title.${type}`)}</header>
      <div className="min-h-0 flex-1 overflow-auto p-6">
        <p className="mx-auto mb-4 max-w-2xl text-sm text-gray-600">{t(`CrmCommunication.Form.Intro.${type}`)}</p>
        <div className="mx-auto max-w-2xl space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{titleLabel}</label>
            <input className="w-full rounded border border-gray-300 px-3 py-2 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{bodyLabel}</label>
            <textarea className="min-h-[160px] w-full rounded border border-gray-300 px-3 py-2 text-sm" value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} />
            <p className="mt-1 text-xs text-gray-500">{t('CrmCommunication.Form.BodyHint')}</p>
          </div>
          {type === 'announcement' ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">{t('CrmCommunication.Form.Field.Recipients')}</label>
              <MultiSelect
                value={recipientCompanyIds}
                options={companyOptions}
                onChange={(e) => onRecipientCompaniesChange(e.value)}
                optionLabel="label"
                optionValue="value"
                display="chip"
                className="w-full text-sm"
                filter
                placeholder={t('CrmCommunication.Form.RecipientsPlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-500">{t('CrmCommunication.Form.RecipientsBuyersHint')}</p>
              {recipientRows.length > 0 ? (
                <div className="mt-3 space-y-2 rounded border border-gray-100 bg-gray-50 p-3">
                  {recipientRows.map((row) => (
                    <div key={row.company_id} className="flex flex-wrap items-end gap-3 border-b border-gray-200 pb-2 last:border-b-0 last:pb-0">
                      <div className="min-w-[140px] flex-1 pt-1 text-sm font-medium text-gray-800">{companyName(row.company_id)}</div>
                      <div className="min-w-[200px] flex-[2]">
                        <label className="mb-1 block text-xs text-gray-600">{t('CrmCommunication.Form.Field.Buyer')}</label>
                        <Dropdown
                          value={row.company_contact_id}
                          options={(contactsByCompany[row.company_id] || []).map((ct) => ({
                            label: ct.name || `ID ${ct.id}`,
                            value: ct.id,
                          }))}
                          onChange={(e) => {
                            const v = e.value;
                            setRecipientRows((prev) =>
                              prev.map((r) => (r.company_id === row.company_id ? { ...r, company_contact_id: v ?? null } : r)),
                            );
                          }}
                          showClear
                          filter
                          placeholder={t('CrmCommunication.Form.BuyerPlaceholder')}
                          className="w-full text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('CrmCommunication.Form.Field.PrimaryCustomer')}</label>
                <Dropdown
                  value={primaryCompanyId}
                  options={companyOptions}
                  onChange={(e) => setPrimaryCompanyId(e.value)}
                  className="w-full text-sm"
                  filter
                  showClear
                  placeholder={t('CrmCommunication.Form.PrimaryPlaceholder')}
                />
              </div>
              {primaryCompanyId ? (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">{t('CrmCommunication.Form.Field.Buyer')}</label>
                  <Dropdown
                    value={primaryCompanyContactId}
                    options={primaryContactOptions.map((ct) => ({ label: ct.name || `ID ${ct.id}`, value: ct.id }))}
                    onChange={(e) => setPrimaryCompanyContactId(e.value)}
                    className="w-full text-sm"
                    filter
                    showClear
                    placeholder={t('CrmCommunication.Form.BuyerPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('CrmCommunication.Form.BuyerOptionalHint')}</p>
                </div>
              ) : null}
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t('CrmCommunication.Form.Field.Scheduled')}</label>
            <input type="datetime-local" className="rounded border border-gray-300 px-3 py-2 text-sm" value={scheduled} onChange={(e) => setScheduled(e.target.value)} />
            <p className="mt-1 text-xs text-gray-500">{t('CrmCommunication.Form.ScheduledHint')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox inputId="crm-comm-submitter-same" checked={submitterSame} onChange={(e) => setSubmitterSame(!!e.checked)} />
            <label htmlFor="crm-comm-submitter-same" className="cursor-pointer text-sm text-gray-800">
              {t('CrmCommunication.Form.SubmitterSame')}
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" disabled={saving} className="rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50" onClick={submit}>
              {t('CrmCommunication.Form.Submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
